import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updateProfile,
  signOut as firebaseSignOut,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { AuthContextType } from '../types/auth';
import { User } from '../types/user';
import { userService } from '../services/userService';
import toast from 'react-hot-toast';
import { useLanguage } from './LanguageContext';
import { doc, updateDoc, setDoc } from 'firebase/firestore';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<(FirebaseUser & User) | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  // Update the lastLoginAt field for the user
  const updateLastLogin = async (userId: string) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastLoginAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating last login time:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      try {
        console.log('[AuthContext] Auth state változás:', {
          firebaseUserExists: !!firebaseUser,
          email: firebaseUser?.email || 'nincs email'
        });

        if (firebaseUser) {
          console.log('[AuthContext] Felhasználó adatok betöltése...');
          const userData = await userService.createUserIfNotExists(firebaseUser);
          
          console.log('[AuthContext] Firestore felhasználó adatok:', {
            id: userData.id,
            email: userData.email,
            role: userData.role,
            organizationId: userData.organizationId,
            permissions: userData.permissions
          });
          
          // Update the lastLoginAt timestamp
          await updateLastLogin(firebaseUser.uid);
          
          // Include the lastLoginAt in the user data
          const updatedUserData = {
            ...userData,
            lastLoginAt: new Date()
          };
          
          const finalUser = { ...firebaseUser, ...updatedUserData };
          console.log('[AuthContext] Végleges felhasználó objektum beállítása:', {
            id: finalUser.id,
            uid: finalUser.uid,
            email: finalUser.email,
            role: finalUser.role,
            organizationId: finalUser.organizationId
          });
          
          setUser(finalUser);
        } else {
          console.log('[AuthContext] Nincs bejelentkezett felhasználó');
          setUser(null);
        }
      } catch (error) {
        console.error('[AuthContext] Hiba a felhasználó beállítása során:', error);
        toast.error(t('common.messages.authContext.error'));
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    
    // Google Calendar hozzáférés kérése
    // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
    // provider.addScope('https://www.googleapis.com/auth/calendar');
    // provider.addScope('https://www.googleapis.com/auth/calendar.events');
    
    // Mindig kérje el a hozzáférést (ne használja a cached tokent)
    // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
    // provider.setCustomParameters({
    //   prompt: 'consent',
    //   access_type: 'offline' // Refresh token kérése
    // });
    
    try {
      const result = await signInWithPopup(auth, provider);
      
      // Mentjük a Google OAuth tokent a Firestore-ba
      // KIKOMMENTEZVE: Könnyebb bejelentkezés érdekében
      // if (result.user) {
      //   const credential = GoogleAuthProvider.credentialFromResult(result);
      //   
      //   if (credential) {
      //     const accessToken = credential.accessToken;
      //     // A refresh token sajnos nem érhető el a Firebase Auth-ban közvetlenül
      //     // Ezért használjuk az access tokent és frissítjük amikor lejár
      //     
      //     try {
      //       await setDoc(doc(db, 'userTokens', result.user.uid), {
      //         googleAccessToken: accessToken,
      //         updatedAt: new Date(),
      //         provider: 'google.com'
      //       }, { merge: true });
      //       
      //       console.log('Google OAuth token saved successfully');
      //     } catch (tokenError) {
      //       console.error('Error saving OAuth token:', tokenError);
      //     }
      //   }
      //   
      //   await updateLastLogin(result.user.uid);
      // }
      
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      toast.success(t('common.messages.authContext.signInSuccess'));
    } catch (error) {
      console.error('Error signing in with Google:', error);
      toast.error(t('common.messages.authContext.signInError'));
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Update last login time when user signs in
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      toast.success(t('common.messages.authContext.signInSuccess'));
      return { success: true };
    } catch (error: any) {
      console.error('Error signing in with email:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = t('common.messages.authContext.signInError');
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Nem található felhasználó ezzel az e-mail címmel.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Hibás jelszó.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Túl sok sikertelen próbálkozás. Próbáld újra később.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Ez a felhasználói fiók le van tiltva.';
          break;
        default:
          errorMessage = `Bejelentkezési hiba: ${error.message}`;
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name if provided
      if (displayName && result.user) {
        await updateProfile(result.user, { displayName });
      }
      
      // Update last login time when user signs up
      if (result.user) {
        await updateLastLogin(result.user.uid);
      }
      
      toast.success('Sikeres regisztráció! Üdvözlünk a YouCRM-ben!');
      return { success: true };
    } catch (error: any) {
      console.error('Error signing up with email:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Regisztrációs hiba történt.';
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Ez az e-mail cím már használatban van.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        case 'auth/weak-password':
          errorMessage = 'A jelszó túl gyenge. Legalább 6 karakter szükséges.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'E-mail regisztráció nincs engedélyezve.';
          break;
        default:
          errorMessage = `Regisztrációs hiba: ${error.message}`;
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Jelszó visszaállító e-mail elküldve! Ellenőrizd a postafiókod.');
      return { success: true };
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      
      let errorMessage = 'Hiba a jelszó visszaállító e-mail küldése során.';
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Nem található felhasználó ezzel az e-mail címmel.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Érvénytelen e-mail cím formátum.';
          break;
        default:
          errorMessage = `Hiba: ${error.message}`;
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      toast.success(t('common.messages.authContext.signOutSuccess'));
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error(t('common.messages.authContext.signOutError'));
    }
  };

  const value: AuthContextType = {
    user,
    currentUser: user,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    signOut
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 